// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * RFC 4180 CSV, with the hardening a findings file needs — plus one rule specific to this tool.
 *
 * THE RULE SPECIFIC TO THIS TOOL. A findings file about invisible characters must not itself
 * contain invisible characters. Writing the raw context window into a cell would put the zero-width
 * joiners and the tag characters straight back into the artefact, where they are once again
 * unfindable: you could not grep the report, and pasting a row into a ticket would carry the
 * payload with it. So every code point charsight considers invisible or deceptive is rendered in
 * the CSV as a visible `‹U+200B›` token by `visibleText()` before it ever reaches a cell.
 *
 * The rest is the same hardening every artefact in this fleet uses:
 *  - control characters become VISIBLE escapes, so one finding is always exactly one physical
 *    line. That is a deliberate deviation from RFC 4180's quoted-newline rule: a findings.csv whose
 *    row count differs from its line count cannot be `wc -l`'d or `grep`'d, and being grepped is
 *    the point of the file.
 *  - `;` triggers quoting, because German, French and Spanish Excel locales split on it.
 *  - a leading `=`, `+`, `-` or `@` gets a `'` prefix. CSV formula injection is a real path from
 *    "here is a findings report" to code execution on the reviewer's machine.
 */

function escapeControls(v: string): string {
  let out = '';
  for (let i = 0; i < v.length; i++) {
    const c = v.charCodeAt(i);
    if (c === 92) out += '\\\\';
    else if (c === 13) out += '\\r';
    else if (c === 10) out += '\\n';
    else if (c === 9) out += '\\t';
    else if (c < 32 || c === 127) out += '\\x' + c.toString(16).padStart(2, '0');
    else out += v[i];
  }
  return out;
}

export function csvField(v: string): string {
  let s = escapeControls(v);
  if (/^[=+\-@]/.test(s)) s = "'" + s;
  if (/[",;]|^\s|\s$/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

export function csvRow(fields: readonly string[]): string {
  return fields.map(csvField).join(',') + '\r\n';
}

/** UTF-8 BOM, or Excel on Windows mojibakes every non-ASCII cell. */
export const CSV_BOM = '﻿';

/**
 * The `consequence` column is a SLUG, never a number.
 *
 * A numeric tier in a spreadsheet is an invitation to rank: `tier <= 3`, sort ascending, pivot by
 * file — and that filter produces a list of files ordered by apparent suspicion in which a narrow
 * no-break space sits two rows below tag smuggling on one shared scale. That is precisely the
 * accusation the honesty gate exists to prevent, exported in the format designed for automated
 * triage and forwarded to someone who never saw the interface. An unrankable slug makes the
 * comparison operator return nothing useful, which is the point.
 */
export const FINDINGS_HEADER = [
  'file',
  'line',
  'column',
  'byte_offset',
  'byte_length',
  'code_point',
  'unicode_name',
  'block',
  'script',
  'category',
  'consequence',
  'confidence',
  'reason',
  'decoded_payload',
  'action',
  'replacement',
  'context',
  'ucd_version',
] as const;

export type FindingsColumn = (typeof FINDINGS_HEADER)[number];

/**
 * Every field is already a string.
 *
 * That is deliberate: line, column and byte offset are BLANK for a PDF row rather than zero,
 * because a content stream has no meaningful line number and fabricating one would make the column
 * a lie in the file most likely to be read by a machine.
 */
export interface FindingRow {
  readonly file: string;
  readonly line: string;
  readonly column: string;
  readonly byteOffset: string;
  readonly byteLength: string;
  readonly codePoint: string;
  readonly unicodeName: string;
  readonly block: string;
  readonly script: string;
  readonly category: string;
  readonly consequence: string;
  readonly confidence: string;
  readonly reason: string;
  readonly decodedPayload: string;
  readonly action: string;
  readonly replacement: string;
  readonly context: string;
  readonly ucdVersion: string;
}

export function findingsCsv(rows: Iterable<FindingRow>): string {
  let out = CSV_BOM + csvRow(FINDINGS_HEADER);
  for (const r of rows) {
    const all: Record<FindingsColumn, string> = {
      file: r.file,
      line: r.line,
      column: r.column,
      byte_offset: r.byteOffset,
      byte_length: r.byteLength,
      code_point: r.codePoint,
      unicode_name: r.unicodeName,
      block: r.block,
      script: r.script,
      category: r.category,
      consequence: r.consequence,
      confidence: r.confidence,
      reason: r.reason,
      decoded_payload: r.decodedPayload,
      action: r.action,
      replacement: r.replacement,
      context: r.context,
      ucd_version: r.ucdVersion,
    };
    out += csvRow(FINDINGS_HEADER.map((h) => all[h]));
  }
  return out;
}
